"use client";
import { useTranslations } from "next-intl";
import { IBaseLabelValue, TCreditCard } from "@/interfaces";
import { creditCardSchema } from "@/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import ResultContent from "../../resultContent";
import { cn } from "@/lib";
import { useSession } from "next-auth/react";
import { routes } from "@/routes";
import { useRouter } from "next/navigation";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import DetailCreditCard from "../../detailCreditCard";
import DetailInquiryPdf from "../../pdfTemplates/detailInquiryPdf";
import { useRef } from "react";
import { IRawPurchasedCardToIbanInquiryResData } from "@/interfaces/inquiries/bank";

const CardToIbanInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedCardToIbanInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const { bank, depositNumber, iban, owners, status } = data.data.data;
  const { number } = data.data.input;
  const { data: session } = useSession();
  const router = useRouter();
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const { getValues } = useForm<TCreditCard>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: {
      input1: number.slice(0, 4),
      input2: number.slice(4, 8),
      input3: number.slice(8, 12),
      input4: number.slice(12, 16),
    },
  });
  const joinedCardNumber =
    getValues().input1 +
    " " +
    getValues().input2 +
    " " +
    getValues().input3 +
    " " +
    getValues().input4;
  const joinedOwners = owners
    .map(({ firstName, lastName }) => `${firstName} ${lastName}`)
    .join(",");
  const formattedIban = iban.replace(/^IR/, "");
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("cardToIban.ibanNumber"),
      value: iban,
    },
    {
      label: t("cardToIban.accountNumber"),
      value: depositNumber,
    },
    {
      label: t("cardToIban.bankName"),
      value: bank,
    },
    {
      label: t("cardToIban.accountOwners"),
      value: joinedOwners,
    },
    {
      label: t("cardToIban.accountStatus"),
      value: status,
    },
    {
      label: t("cardToIban.submittedCardNumber"),
      value: number,
    },
  ];

  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("cardToIban.title")}
        </h2>
        <div className="mx-auto mb-8 sm:w-3/4">
          <DetailCreditCard
            variant="iban"
            owners={joinedOwners}
            cardValues={{
              label: t("cardToIban.ibanNumber"),
              value: formattedIban,
            }}
            headerRight={() => (
              <div className="flex flex-col items-end gap-1">
                <span className="text-micro font-light">شماره کارت</span>
                <span className="text-xs font-semibold" dir="ltr">
                  {joinedCardNumber}
                </span>
              </div>
            )}
            footer={() => (
              <>
                <div className="flex w-full justify-between">
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">شماره حساب</span>
                    <span className="md:text-md text-sm font-semibold">
                      {depositNumber}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-light">وضعیت حساب</span>
                    <span className="md:text-md text-sm font-semibold">
                      {status}
                    </span>
                  </div>
                </div>
              </>
            )}
          />
        </div>
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center font-bold lg:text-lg">
            {t("cardToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="card-to-iban-inquiry-download-pdf"
            resultContent={resultContent}
            fileName="کارت-به-شبا"
            tableTitle="کارت به شبا"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>
        {session?.user.token && <ResultContent content={resultContent} />}
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            useHtmlToPdf={true}
            id="card-to-iban-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            fileName="کارت-به-شبا"
            tableTitle="کارت به شبا"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-card-to-iban-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-card-to-iban-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Card_To_Iban.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <DetailInquiryPdf
          subtitle="تبدیل شماره کارت به شبا"
          code={data.code}
          displayComponent={{
            variant: "iban",
            cardNumber: number,
            ownerName: joinedOwners,
            mainValue: {
              label: "شماره شبا",
              value: formattedIban,
            },
            headerRight: {
              label: "شماره کارت",
              value: joinedCardNumber,
            },
            bankName: bank,
            additionalFields: [
              {
                label: "شماره حساب",
                value: depositNumber,
              },
              {
                label: "وضعیت حساب",
                value: status,
              },
            ],
          }}
          resultContent={resultContent}
          showResultSection={true}
        />
      </div>
    </div>
  );
};

export default CardToIbanInquiryDetails;
